import { Avatar, Stack } from '@mui/material'
import { useEffect, useMemo } from 'react'
import { Image } from '@mui/icons-material'

import { InlinePopoverEditor } from './InlinePopoverEditor'

import { CardActivityType, type CardActivityWithId } from '@/models/card-activity/card-activity.model'
import { debounce } from '@/hooks/useDebounce'
import { PlayTTSButton } from '@/components/PlayTTSButton'
import { useTTS } from '@/hooks/useTTS'

interface PromptImageProps {
  card: CardActivityWithId
  updateCard: (id: string, field: keyof CardActivityWithId, value: Partial<CardActivityWithId>) => void
}

export default function PromptImage({ card, updateCard }: PromptImageProps) {
  const { playing, startTTS } = useTTS({
    text: card.prompt ?? '',
    language: card?.language ?? undefined,
  })
  const propertyToUpdate = (() => {
    switch (card.type) {
      case CardActivityType.READ_REPEAT:
        return 'target_text'
      case CardActivityType.READ_RESPOND:
      case CardActivityType.RESPOND_WRITE:
      case CardActivityType.SHORT_ANSWER:
        return 'prompt'
      case CardActivityType.MULTIPLE_CHOICE:
        return 'question'
      default:
        return 'target_text'
    }
  })()

  const textValue = card[propertyToUpdate] ?? ''
  const hasTTS = card?.allowTTS === true

  const debouncedUpdateCard = useMemo(() => {
    return debounce((id: string, field: keyof CardActivityWithId, value: Partial<CardActivityWithId>) => {
      updateCard(id, field, value)
      updateCard(id, 'verificationStatus', null)
      updateCard(id, 'checked', false)
    }, 500)
  }, [updateCard])

  useEffect(() => {
    return () => {
      debouncedUpdateCard.cancel?.()
    }
  }, [debouncedUpdateCard])

  const handleChange = (value: Partial<CardActivityWithId>) => {
    debouncedUpdateCard(card.id, propertyToUpdate, value)
  }

  return (
    <Stack alignItems="center" direction="row" gap="8px">
      {card.image?.url ? (
        <Avatar src={card.image?.url ?? ''}>
          <Image />
        </Avatar>
      ) : null}
      {hasTTS ? <PlayTTSButton playing={playing} startTTS={startTTS} /> : null}
      <InlinePopoverEditor
        label={textValue || 'Click to edit'}
        placeholder="Enter here..."
        showUnderline={false}
        value={textValue}
        onSave={newValue => {
          handleChange(newValue)
        }}
      />
    </Stack>
  )
}
